import { axiosSwagger } from "@/axios";

export const state = {
    loading: false,
    data: [],
    total: 0,
    sessions: [],
    sessionsLoading: false,
    currentSessionId: 0,
    currentSessionTitle: '',
    sessionMessages: [],
    sessionMessagesLoading: false,
    documentData: null,
    documentDataLoading: false,
    fileData: null,
}


export const getters = {
}


export const mutations = {
    loading: (state, loading) => {
        state.loading = loading;
    },
    setData: (state, data) => {
        state.data = data;
    },
    setTotal: (state, total) => {
        state.total = total;
    },
    setSessions: (state, sessions) => {
        state.sessions = sessions;
    },
    setSessionsLoading: (state, loading) => {
        state.sessionsLoading = loading;
    },
    setCurrentSessionId: (state, sessionId) => {
        state.currentSessionId = sessionId;
    },
    setCurrentSessionTitle: (state, title) => {
        state.currentSessionTitle = title;
    },
    setCurrentSession: (state, session) => {
        const index = state.sessions.findIndex(
            (item) => item.id === session.id
        );

        if (index !== -1) {
            // Use splice to maintain reactivity in Vue 2
            state.sessions.splice(index, 1, session);
        }
        else {
            state.sessions.unshift(session);
        }
    },
    setSessionMessages: (state, messages) => {
        state.sessionMessages = messages;
    },
    setSessionMessagesLoading: (state, loading) => {
        state.sessionMessagesLoading = loading;
    },
    setDocumentData: (state, data) => {
        state.documentData = data;
    },
    setDocumentDataLoading: (state, loading) => {
        state.documentDataLoading = loading;
    },
    removeSession: (state, sessionId) => {
        state.sessions = state.sessions.filter(session => session.id !== sessionId);
    },
    setFileData: (state, data) => {
        state.fileData = data;
    },
}


function getAuthHeaders() {
    const authToken = localStorage.getItem("TOKEN");
    const headers = { 'Content-Type': 'application/json' };
    if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
    }
    return headers;
}

function getUserDeliveryUnitId() {
    const userData = localStorage.getItem("USER") !== null && localStorage.getItem("USER") !== ''
        ? JSON.parse(localStorage.getItem("USER"))
        : null;
    return userData ? userData.deliveryUnit?.id : null;
}


export const actions = {
    async sendChatMessage({ commit, state: moduleState }, { text, documentId, onChunk }) {
      commit("loading", true);

      const deliveryUnitId = getUserDeliveryUnitId();

      if (!deliveryUnitId) {
        commit("loading", false);
        throw new Error('User delivery unit not found');
      }

      const documentIdParam = documentId || 0;
      const sessionId = moduleState.currentSessionId || 0;
      const baseURL = axiosSwagger.defaults.baseURL || window.location.origin;
      const url = `${baseURL}/api/DocumentChat/SendChatMessage/${deliveryUnitId}/${documentIdParam}/${sessionId}`;

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ text })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let reading = true;
        let fullText = '';

        while (reading) {
          const { done, value } = await reader.read();
          
          if (done) {
            reading = false;
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.trim().startsWith('data: ')) {
              try {
                const jsonData = JSON.parse(line.trim().substring(6));
                
                if (jsonData.type === 'message' && onChunk) {
                  fullText += jsonData.data;
                  onChunk(jsonData);
                } else if (jsonData.type === 'citations' && onChunk) {
                  onChunk(jsonData);
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e);
              }
            }
          }
        }

        commit("setData", fullText);
        commit("loading", false);
        
        return { success: true, text: fullText };
      } catch (error) {
        commit("loading", false);
        throw error;
      }
    },

    async fetchChatSessions({ commit }) {
      commit("setSessionsLoading", true);
      try {
        const response = await axiosSwagger.get('/api/ChatSession/All');
        const sessions = response.data.sort((a, b) => b.id - a.id) || [];
        commit("setSessions", sessions);
        return sessions;
      } catch (error) {
        console.error('Error fetching chat sessions:', error);
        commit("setSessions", []);
        throw error;
      } finally {
        commit("setSessionsLoading", false);
      }
    },

    async fetchSessionMessages({ commit }, sessionId) {
      commit("setSessionMessagesLoading", true);
      try {
        const response = await axiosSwagger.get(`/api/ChatSession/Messages/${sessionId}`);
        const messages = response.data || [];
        commit("setSessionMessages", messages);
        return messages;
      } catch (error) {
        console.error('Error fetching session messages:', error);
        commit("setSessionMessages", []);
        throw error;
      } finally {
        commit("setSessionMessagesLoading", false);
      }
    },

    // eslint-disable-next-line no-unused-vars
    async fetchSessionTitle({ commit }, sessionId) {
      try {
        const response = await axiosSwagger.get(`/api/DocumentChat/Title/${sessionId}`, {
          responseType: 'text',
        });
        const title = response.data || '';
        return title;
      } catch (error) {
        console.error('Error fetching session title:', error);
        throw error;
      }
    },

    setCurrentSession({ commit }, session) {
      commit("setCurrentSession", session);
      if (session.title) {
        commit("setCurrentSessionTitle", session.title);
      }
    },

    startNewSession({ commit }) {
      commit("setCurrentSessionId", 0);
      commit("setCurrentSessionTitle", '');
      commit("setSessionMessages", []);
    },

    // Called from file.vue dropdown "AI Chat" action and drag-drop
    setDocumentData({ commit }, documentData) {
      commit("setDocumentData", documentData || null);
    },

    setFileData({ commit }, fileData) {
      commit("setFileData", fileData || null);
    },

    clearDocumentData({ commit }) {
      commit("setDocumentData", null);
      commit("setFileData", null);
    },

    async fetchDocumentById({ commit }, documentId) {
      if (!documentId) return null;
      commit("setDocumentDataLoading", true);
      try {
        const response = await axiosSwagger.get(`/api/FileManager/${documentId}`);
        const data = response.data || null;
        commit("setDocumentData", data);
        return data;
      } catch (error) {
        console.error('Error fetching document data:', error);
        commit("setDocumentData", null);
        throw error;
      } finally {
        commit("setDocumentDataLoading", false);
      }
    },

    async deleteSession({ commit }, sessionId) {
      try {
        await axiosSwagger.delete(`/api/ChatSession/Remove/${sessionId}`);
        commit('removeSession', sessionId);
        return true;
      } catch (error) {
        console.error('Error deleting chat session:', error);
        throw error;
      }
    }
}